import React, { useCallback, useEffect, useRef, useState } from 'react';
import api from '../api';

// Podpisová plocha — kreslí sa prstom (mobil/tablet), stylusom alebo myšou.
// Ukladá PNG s PRIEHĽADNÝM pozadím, aby sa dal podpis natlačiť na faktúru bez
// bieleho obdĺžnika. Pointer events pokrývajú všetky tri vstupy naraz.
//
// Používa sa na dvoch miestach s rôznym endpointom:
//   - firemný podpis (default /settings/signature) — admin nastavenia, tlačí
//     sa na vystavené faktúry
//   - osobný podpis (/settings/my-signature) — každý schvaľovateľ svoj,
//     tlačí sa do schvaľovacej pečiatky na PDF dokladu

interface Props {
  onClose: () => void;
  onSaved: () => void;
  hasExisting: boolean;
  endpoint?: string;      // default firemný podpis
  title?: string;
  description?: string;
}

const SignaturePad: React.FC<Props> = ({
  onClose, onSaved, hasExisting,
  endpoint = '/settings/signature',
  title = 'Podpis firmy',
  description = 'Podpíš sa prstom (na mobile či tablete), stylusom alebo myšou. Podpis sa uloží raz a použije sa všade, kde ho systém potrebuje — dnes na vystavených faktúrach.',
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const drawing = useRef(false);
  const dirty = useRef(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [empty, setEmpty] = useState(true);

  // Plátno kreslíme vo fyzických pixeloch displeja, inak je čiara na mobile rozmazaná
  const setupCanvas = useCallback(() => {
    const c = canvasRef.current;
    if (!c) return;
    const dpr = window.devicePixelRatio || 1;
    const rect = c.getBoundingClientRect();
    c.width = Math.round(rect.width * dpr);
    c.height = Math.round(rect.height * dpr);
    const ctx = c.getContext('2d');
    if (!ctx) return;
    ctx.scale(dpr, dpr);
    ctx.lineWidth = 2.2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#111827';
  }, []);

  useEffect(() => { setupCanvas(); }, [setupCanvas]);

  const pos = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const start = (e: React.PointerEvent<HTMLCanvasElement>) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    drawing.current = true;
    const p = pos(e);
    ctx.beginPath();
    ctx.moveTo(p.x, p.y);
  };

  const move = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    const p = pos(e);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    if (!dirty.current) { dirty.current = true; setEmpty(false); }
  };

  const end = () => { drawing.current = false; };

  const clear = () => {
    const c = canvasRef.current;
    const ctx = c?.getContext('2d');
    if (!c || !ctx) return;
    ctx.clearRect(0, 0, c.width, c.height);
    dirty.current = false;
    setEmpty(true);
  };

  const save = async () => {
    const c = canvasRef.current;
    if (!c || empty) { setError('Najprv sa podpíš'); return; }
    setSaving(true);
    setError(null);
    try {
      const blob: Blob = await new Promise((resolve, reject) =>
        c.toBlob(b => (b ? resolve(b) : reject(new Error('Nepodarilo sa vytvoriť obrázok'))), 'image/png'));
      const fd = new FormData();
      fd.append('signature', blob, 'signature.png');
      await api.post(endpoint, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      onSaved();
    } catch (e: any) {
      setError(e.response?.data?.error || 'Uloženie zlyhalo');
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm('Naozaj odstrániť uložený podpis? Na ďalších dokladoch už nebude.')) return;
    setSaving(true);
    try {
      await api.delete(endpoint);
      onSaved();
    } catch (e: any) {
      setError(e.response?.data?.error || 'Mazanie zlyhalo');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1300, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div onClick={e => e.stopPropagation()}
        style={{ background: '#fff', borderRadius: 8, width: '95vw', maxWidth: 560, padding: '16px 20px', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <h3 style={{ margin: 0, fontSize: 16 }}>{title}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: '#6b7280', lineHeight: 1 }}>×</button>
        </div>

        <p style={{ margin: '0 0 10px', fontSize: 12.5, color: '#6b7280' }}>
          {description}
        </p>

        <canvas
          ref={canvasRef}
          onPointerDown={start}
          onPointerMove={move}
          onPointerUp={end}
          onPointerLeave={end}
          onPointerCancel={end}
          style={{
            width: '100%', height: 200, border: '2px dashed #d1d5db', borderRadius: 8,
            background: '#fff', touchAction: 'none', cursor: 'crosshair', display: 'block',
          }}
        />
        <div style={{ fontSize: 11, color: '#9ca3af', margin: '4px 0 12px', textAlign: 'center' }}>
          {empty ? 'plocha na podpis' : 'podpis pripravený'}
        </div>

        {error && <div style={{ color: '#dc2626', fontSize: 13, marginBottom: 8 }}>{error}</div>}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            {hasExisting && (
              <button onClick={remove} disabled={saving}
                style={{ padding: '8px 12px', background: '#fff', color: '#dc2626', border: '1px solid #fca5a5', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>
                Odstrániť uložený
              </button>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={clear} disabled={saving}
              style={{ padding: '8px 12px', background: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>
              Vymazať
            </button>
            <button onClick={save} disabled={saving || empty}
              style={{ padding: '8px 16px', background: empty ? '#9ca3af' : '#16a34a', color: '#fff', border: 'none', borderRadius: 6, cursor: empty ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 600 }}>
              {saving ? 'Ukladám…' : 'Uložiť podpis'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignaturePad;
